import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function RequirementDetails() {
  const { projectKey, requirementId } = useParams();
  const navigate = useNavigate();
  const [requirement, setRequirement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchRequirement();
  }, [projectKey, requirementId]);

  const fetchRequirement = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/requirements/${requirementId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        if (response.status === 404) {
          setError('Requirement not found');
        } else {
          setError('Failed to load requirement');
        }
        return;
      }
      const data = await response.json();
      setRequirement(data.data || data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <strong>Error:</strong> {error}
        </div>
        <Link to={`/projects/${projectKey}/requirements`} className="btn btn-outline-primary">
          ← Back to Requirements
        </Link>
      </div>
    );
  }

  if (!requirement) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning">Requirement not found</div>
        <Link to={`/projects/${projectKey}/requirements`} className="btn btn-outline-primary">
          ← Back to Requirements
        </Link>
      </div>
    );
  }

  const badgeColor = {
    critical: 'danger',
    high: 'warning',
    medium: 'info',
    low: 'secondary',
  };

  const statusColor = {
    draft: 'secondary',
    approved: 'info',
    implemented: 'success',
    verified: 'success',
    deprecated: 'danger',
  };

  return (
    <div className="container mt-4 mb-4">
      <div className="row mb-4">
        <div className="col-md-8">
          <Link to={`/projects/${projectKey}/requirements`} className="text-muted text-decoration-none small mb-2 d-block">
            ← Back to Requirements
          </Link>
          <h2 className="fw-bold mb-1">{requirement.title}</h2>
          <div className="d-flex gap-2 align-items-center flex-wrap mt-3">
            <span className="badge bg-light text-dark fw-semibold border">{requirement.reqId || `REQ-${requirement.id}`}</span>
            <span className={`badge bg-${badgeColor[requirement.priority] || 'secondary'}`}>
              {requirement.priority?.toUpperCase() || 'MEDIUM'}
            </span>
            <span className={`badge bg-${statusColor[requirement.status] || 'secondary'}`}>
              {requirement.status?.replace(/^\w/, c => c.toUpperCase()) || 'DRAFT'}
            </span>
            <span className="badge bg-info">{requirement.type?.toUpperCase() || 'FUNCTIONAL'}</span>
          </div>
        </div>
        <div className="col-md-4 text-end">
          <button className="btn btn-outline-primary btn-sm me-2">Edit</button>
          <button className="btn btn-outline-danger btn-sm">Delete</button>
        </div>
      </div>

      <div className="row">
        <div className="col-md-8">
          <div className="card mb-3">
            <div className="card-header bg-light">
              <h5 className="mb-0">Description</h5>
            </div>
            <div className="card-body">
              {requirement.description ? (
                <p className="text-muted">{requirement.description}</p>
              ) : (
                <p className="text-muted fst-italic">No description provided</p>
              )}
            </div>
          </div>

          {requirement.tags && requirement.tags.length > 0 && (
            <div className="card mb-3">
              <div className="card-header bg-light">
                <h5 className="mb-0">Tags</h5>
              </div>
              <div className="card-body">
                <div className="d-flex gap-2 flex-wrap">
                  {requirement.tags.map((tag, idx) => (
                    <span key={idx} className="badge bg-secondary">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {requirement.jira?.issueKey && (
            <div className="card mb-3">
              <div className="card-header bg-light">
                <h5 className="mb-0">JIRA Integration</h5>
              </div>
              <div className="card-body">
                <p className="mb-2">
                  <strong>Issue Key:</strong> <a href={requirement.jira.url} target="_blank" rel="noopener noreferrer">{requirement.jira.issueKey}</a>
                </p>
                {requirement.jira.status && (
                  <p className="mb-0">
                    <strong>Status:</strong> <span className="badge bg-info">{requirement.jira.status}</span>
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="col-md-4">
          <div className="card">
            <div className="card-header bg-light">
              <h5 className="mb-0">Details</h5>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <p className="text-muted small mb-1">Folder</p>
                <p className="mb-0">{requirement.folderId?.name || requirement.folder || '—'}</p>
              </div>
              <hr />
              <div className="mb-3">
                <p className="text-muted small mb-1">Type</p>
                <p className="mb-0">{requirement.type?.replace(/^\w/, c => c.toUpperCase()) || '—'}</p>
              </div>
              <hr />
              <div className="mb-3">
                <p className="text-muted small mb-1">Priority</p>
                <p className="mb-0">{requirement.priority?.replace(/^\w/, c => c.toUpperCase()) || '—'}</p>
              </div>
              <hr />
              <div className="mb-3">
                <p className="text-muted small mb-1">Status</p>
                <p className="mb-0">{requirement.status?.replace(/^\w/, c => c.toUpperCase()) || '—'}</p>
              </div>
              <hr />
              <div className="mb-0">
                <p className="text-muted small mb-1">Created</p>
                <p className="mb-0 small">{new Date(requirement.createdAt).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
